import type { QuestionFigure } from '@padcanvas/shared';
/**
 * Wire types for the hint-quality views.
 *
 * Mirrors the responses of `GET /hint-quality/questions` and
 * `GET /hint-quality/questions/{id}`. Kept local to shared-ui (not imported
 * from the backend) so the web bundle doesn't pull in server types — the same
 * convention the other shared-ui feature folders follow.
 *
 * See docs/FeatureDesigns/HintSystem/hint-quality-review.md §5.
 */

export type HintLevel = 'nudge' | 'direction' | 'guided' | 'reveal';

export type HintCategory =
  | 'next_step' | 'where_wrong' | 'explain_concept'
  | 'smaller_hint' | 'right_direction';

export type HintDetectedState =
  | 'no_attempt' | 'progressed' | 'stuck' | 'confused'
  | 'error' | 'needs_clarification';

export type HintStepDifficulty = 'easy' | 'medium' | 'hard';

export interface HintFeedback {
  rating: 'positive' | 'negative';
  comment?: string;
  submittedAt: string;
}

/** An educator's verdict on the hint's quality — a different judgement from the
 *  student's "did it help me", kept in its own field so neither overwrites the
 *  other. */
export interface HintReviewerFeedback {
  rating: 'positive' | 'negative';
  comment?: string;
  submittedAt: string;
  reviewerId: string;
  reviewerName: string;
  reviewerRole: 'teacher' | 'admin';
}

/** One row of the rollup — a question hints have been shown on. */
export interface HintQualityRow {
  questionId: string;
  questionText: string;
  subject?: string;
  topic?: string;
  students: number;
  sessions: number;
  hintsShown: number;
  hintsLlm: number;
  hintsPrewritten: number;
  avgHintsPerSession: number;
  thumbsUp: number;
  thumbsDown: number;
  unrated: number;
  /** Educator verdicts, counted apart from the student's thumbs. */
  reviewedGood: number;
  reviewedPoor: number;
  /** Hints no educator has looked at yet — the review backlog. */
  unreviewed: number;
  /** Sessions bucketed by the deepest level any of their LLM turns reached. */
  maxLevelReached: Partial<Record<HintLevel, number>>;
  categories: Partial<Record<HintCategory, number>>;
  firstActivityAt: string;
  lastActivityAt: string;
}

/** An assignment hints were actually taken in. Derived server-side from the
 *  hint data, so every option in the dropdown yields at least one row. */
export interface HintQualityAssignmentOption {
  id: string;
  title: string;
  /** When the assignment was given. Shown beside the title and used to order
   *  the list — titles alone rarely identify which one you mean. */
  date?: string;
}

export interface HintQualityRollupResponse {
  items: HintQualityRow[];
  total: number;
  page: number;
  pageSize: number;
  /** What the server actually scoped to, so the UI can label the view without
   *  inferring it from the signed-in role. */
  scope: 'all' | 'students';
  assignments: HintQualityAssignmentOption[];
}

/** One hint the student saw. Discriminated the same way the stored history is —
 *  a prewritten reveal genuinely has no diagnostics to show. */
export type HintQualityTurn =
  | {
      source: 'prewritten';
      /** Position in the stored timeline — how a reviewer addresses this hint. */
      entryIndex: number;
      createdAt: string;
      hintIndex: number;
      hint: string;
      feedback?: HintFeedback;
      reviewerFeedback?: HintReviewerFeedback;
    }
  | {
      source: 'llm';
      entryIndex: number;
      createdAt: string;
      turnIndex: number;
      hint: string;
      category?: HintCategory;
      level?: HintLevel;
      studentMessage?: string;
      typedTextAtHintTime?: string;
      canvasImageUrl?: string;
      hadImageAtHintTime?: boolean;
      yourProgress?: string;
      detectedState?: HintDetectedState;
      detectedIssue?: string;
      difficulty?: HintStepDifficulty;
      confidence?: number;
      workChanged?: boolean;
      feedback?: HintFeedback;
      reviewerFeedback?: HintReviewerFeedback;
    };

export interface HintQualitySession {
  id: string;
  attemptId?: string;
  testSessionId?: string;
  student: { id: string; name: string; grade?: number };
  startedAt: string;
  lastHintAt: string;
  inputMode?: 'draw' | 'text' | 'photo';
  hintsLlm: number;
  hintsPrewritten: number;
  maxLevel?: HintLevel;
  thumbsUp: number;
  thumbsDown: number;
  timeline: HintQualityTurn[];
}

export interface HintQualityQuestionResponse {
  question: {
    id: string;
    questionText: string;
    subject?: string;
    topic?: string;
    questionType?: string;
    questionDiagramUrl?: string;
    figures?: QuestionFigure[] | null;
    /** MCQ options — an MCQ stem without them can't be judged. */
    options?: { label: string; text: string; imageUrl?: string | null; isCorrect?: boolean }[];
    /** Sub-parts of a STRUCTURED question. */
    parts?: { partId: string; statement?: string; figures?: QuestionFigure[] }[];
    correctAnswer?: string;
    /** The question's own prewritten hints — lets the page show which are
     *  actually being revealed and which never get used. */
    hints?: string[];
  } | null;
  sessions: HintQualitySession[];
  scope: 'all' | 'students';
}

/** One row of the session list — a student's single attempt at one question. */
export interface HintQualitySessionRow {
  id: string;
  questionId: string;
  attemptId?: string;
  startedAt: string;
  lastHintAt: string;
  student: { id: string; name: string; email?: string };
  questionText: string;
  subject?: string;
  topic?: string;
  hintsShown: number;
  hintsLlm: number;
  hintsPrewritten: number;
  maxLevel?: HintLevel;
  /** Distinct categories in the order first asked — the sequence is itself a
   *  signal ("next step, next step, where wrong"). */
  categories: HintCategory[];
  thumbsUp: number;
  thumbsDown: number;
  reviewedGood: number;
  reviewedPoor: number;
  unreviewed: number;
  inAssignment: boolean;
  inputMode?: 'draw' | 'text' | 'photo';
  /** Set only when no hint was shown, explaining why. */
  emptyReason?: 'generating' | 'failed' | 'abandoned';
}

/** One row of the per-student view. */
export interface HintQualityStudentRow {
  id: string;
  name: string;
  email?: string;
  sessions: number;
  hintsShown: number;
  hintsLlm: number;
  hintsPrewritten: number;
  questionsWithHints: number;
  questionsAttempted: number;
  thumbsUp: number;
  thumbsDown: number;
  lastHintAt?: string;
}

export interface HintQualityStudentsResponse {
  items: HintQualityStudentRow[];
  total: number;
  page: number;
  pageSize: number;
  scope: 'all' | 'students';
  assignments: HintQualityAssignmentOption[];
}

export interface HintQualityStudentParams {
  page?: number;
  pageSize?: number;
  sinceDate?: string;
  search?: string;
  questionSetId?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface HintQualitySessionsResponse {
  items: HintQualitySessionRow[];
  total: number;
  page: number;
  pageSize: number;
  scope: 'all' | 'students';
  assignments: HintQualityAssignmentOption[];
}

export interface HintQualitySessionParams {
  page?: number;
  pageSize?: number;
  sinceDate?: string;
  subject?: string;
  search?: string;
  context?: string;
  level?: string;
  feedback?: string;
  studentId?: string;
  questionSetId?: string;
  review?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface HintQualityRollupParams {
  page?: number;
  pageSize?: number;
  sinceDate?: string;
  subject?: string;
  search?: string;
  hasNegative?: boolean;
  questionSetId?: string;
  review?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

/** Display order + labels for the level buckets. `nudge` is the gentlest and
 *  `reveal` the most direct, so a distribution skewed right means students
 *  needed to be led further. */
export const HINT_LEVELS: { value: HintLevel; label: string; short: string }[] = [
  { value: 'nudge', label: 'Nudge', short: 'N' },
  { value: 'direction', label: 'Direction', short: 'D' },
  { value: 'guided', label: 'Guided', short: 'G' },
  { value: 'reveal', label: 'Reveal', short: 'R' },
];

export const HINT_CATEGORY_LABELS: Record<HintCategory, string> = {
  next_step: 'Next step',
  where_wrong: 'Where wrong',
  explain_concept: 'Explain concept',
  smaller_hint: 'Smaller hint',
  right_direction: 'Right direction?',
};

/** Shared review-state filter options — the same vocabulary on every tab. */
export const HINT_REVIEW_FILTER_OPTIONS = [
  { value: 'unreviewed', label: 'Needs review' },
  { value: 'poor', label: 'Judged poor' },
  { value: 'good', label: 'Judged good' },
  { value: 'reviewed', label: 'Reviewed (either)' },
];

export const HINT_STATE_LABELS: Record<HintDetectedState, string> = {
  no_attempt: 'No attempt',
  progressed: 'Progressed',
  stuck: 'Stuck',
  confused: 'Confused',
  error: 'Error',
  needs_clarification: 'Needs clarification',
};
